import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { StatusBanner } from "@/components/marketing/status/StatusBanner";
import { ProductCard } from "@/components/marketing/status/ProductCard";
import { portalFetch } from "@/lib/portalApi";
import { Loader2 } from "lucide-react";
import { isPrerendering } from "@/lib/prerender";

export const Route = createFileRoute("/status")({
  component: StatusPage,
});

type ProductStatus = {
  name: string;
  status: string;
};

type StatusResponse = {
  timestamp: string;
  overall_status: string;
  products: ProductStatus[];
};

type DailyUptime = {
  date: string;
  uptime_percent: number;
};

type ProductUptimeHistory = {
  name: string;
  uptime_percent: number;
  daily: DailyUptime[];
};

type HistoryResponse = {
  days: number;
  products: ProductUptimeHistory[];
};

function StatusPage() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Never fetch while prerendering. Gating only the state setters was not
    // enough: in CI the API base is absolute, so the build would call the real
    // production API and could bake live status values into static HTML that
    // is then served, cached, for every visitor until React boots. It also
    // rendered the spinner and the cards simultaneously. The snapshot should
    // show the loading state and nothing else; real visitors fetch on
    // hydration.
    if (isPrerendering()) return;

    async function fetchStatus() {
      try {
        const data = await portalFetch<StatusResponse>("/api/v1/public/status");
        // A 200 with an unexpected body must not reach the render: an
        // unguarded status.products.map crashed the whole page into the error
        // boundary (found prerendering, where the static server answers /api
        // routes with the SPA shell - but any malformed response does it).
        if (!Array.isArray(data?.products)) {
          throw new Error("Malformed status response");
        }
        setStatus(data);
        setError(null);
      } catch {
        setError("Unable to load status data");
      } finally {
        setLoading(false);
      }
    }

    fetchStatus();
    const interval = setInterval(fetchStatus, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isPrerendering()) return;

    async function fetchHistory() {
      try {
        const data = await portalFetch<HistoryResponse>(
          "/api/v1/public/status/history?days=30",
        );
        // Same shape guard as the status fetch: a resolved response that is
        // not the expected shape must not reach the render - see below.
        if (Array.isArray(data?.products)) {
          setHistory(data);
        }
      } catch {
        // History is supplementary - don't block the page
      }
    }

    fetchHistory();
  }, []);

  // ?. on BOTH hops: `history?.products.map` guarded only `history`, so a
  // malformed response that set state crashed the whole page into the error
  // boundary (found prerendering, where the static server answers /api routes
  // with the SPA shell - but any malformed response does it).
  const historyByProduct = new Map(
    history?.products?.map((p) => [p.name, p]) ?? [],
  );

  return (
    <MarketingLayout>
      <Helmet>
        <title>System Status | Larasoft</title>
        <meta
          name="description"
          content="Real-time system status and uptime for Larasoft platform services."
        />
        <link rel="canonical" href="https://larasoft.global/status" />
      </Helmet>

      <Section>
        <div className="mx-auto max-w-3xl space-y-6">
          <h1 className="text-3xl font-bold tracking-tight text-brand-blue-dark">System Status</h1>

          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && !status && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-800">
              {error}
            </div>
          )}

          {status && (
            <>
              <StatusBanner
                status={status.overall_status}
                timestamp={status.timestamp}
              />

              <div className="space-y-3">
                {status.products.map((product) => {
                  const ph = historyByProduct.get(product.name);
                  return (
                    <ProductCard
                      key={product.name}
                      name={product.name}
                      status={product.status}
                      daily={ph?.daily}
                      uptimePercent={ph?.uptime_percent}
                    />
                  );
                })}
              </div>

              {typeof history?.days === "number" && (
                <p className="text-center text-xs text-muted-foreground">
                  Uptime over the past {history.days} days.
                </p>
              )}
            </>
          )}
        </div>
      </Section>
    </MarketingLayout>
  );
}
